import React, { useEffect, useRef, useState } from "react";
import Input from "../../../Common/Components/Input";
import styled from "styled-components";
import SearchTerm from "../../../Content/Search/Components/SearchTerm";
import CopyAll from "../../../../assets/img/icons/CopyAll.svg";
import ExtendedKeywordCount from "modules/Content/Search/Components/ExtendedKeywordCount";
import { useSelector } from "react-redux";
import { RootState } from "store";

type Props = {
  heading: String;
  subText?: String;
  placeholder: String;
  desc: String;
  searchTerms?: Array<string>;
  handleAddSearchTerm?: any;
  handleOnRemove?: any;
  showInput?: boolean;
  isPeople?: boolean;
  desClassName?: string;
  inAdvancedSearch?: boolean;
  deleteAllKeywordsHandler?: () => void;
  includedKeywords?: boolean;
  includedKeywordsCount?: boolean;
  excludedKeywords?: boolean;
  errorIncludedKeywords?: boolean;
  errorMsgIncludedKeywords?: string;
  errorExcludedKeywords?: boolean;
  errorMsgExcludedKeywords?: string;
  errorMsgIncludedKeywordsEmpty?: boolean;
  marginTop?: string;
  marginBottom?: string;
  inNewListAsider?: boolean;
  idprefix?: string;
  hideTopWrapper?: boolean;
  firstKeyPressed?: boolean;
  onKeyPressOpen?: () => void;
};

const KeywordSourceV2: React.FC<Props> = ({
  children,
  heading,
  subText,
  placeholder,
  desc,
  searchTerms,
  handleAddSearchTerm,
  handleOnRemove,
  showInput = true,
  isPeople = false,
  desClassName,
  inAdvancedSearch,
  deleteAllKeywordsHandler,
  includedKeywords,
  includedKeywordsCount,
  excludedKeywords,
  errorIncludedKeywords,
  errorMsgIncludedKeywords,
  errorExcludedKeywords,
  errorMsgExcludedKeywords,
  errorMsgIncludedKeywordsEmpty,
  marginTop,
  marginBottom,
  inNewListAsider,
  idprefix,
  hideTopWrapper,
  firstKeyPressed,
  onKeyPressOpen
}) => {
  const inputElement: any = useRef(null);
  const inputFieldRef: any = useRef(null);

  const dynamicAudienceLoading = useSelector((state: RootState) => state.recommendedPeople.dynamicAudienceLoading);

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: any) {
      if (inputElement.current && !inputElement.current.contains(event.target)) {
        setInputFocused(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputElement]);

  const [value, setValue] = useState("");
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  const copyToClipboard = (e: any) => {
    if (searchTerms && searchTerms?.length > 0) {
      navigator.clipboard.writeText(searchTerms?.join(", ")?.toString());
      setShowCopiedMessage(true);
      setTimeout(() => {
        setShowCopiedMessage(false);
      }, 3000);
    }
  };

  return (
    <Wrapper
      marginTop={marginTop}
      marginBottom={marginBottom}
      dynamicAudienceLoading={dynamicAudienceLoading}
      inAdvanceSearch={inAdvancedSearch}
      inNewListAsider={inNewListAsider}
    >
      <div className="add-words-wrapper pb-3" data-id={"add-words-wrapper-" + idprefix}>
        {hideTopWrapper ? null :
          (
            <div className="wrapper-content">
              <div className="wrapper">
                <h3 className="add-word-head">{heading}</h3>
                {subText && <span className="sub-text">({subText})</span>}
              </div>
              {children}
              {inAdvancedSearch && (
                <div>
                  <span
                    onClick={copyToClipboard}
                    className={`copy-text-style ${searchTerms?.length === 0 ? "disable-item" : ""}`}
                  >
                    <img src={CopyAll} />
                    &nbsp;
                    {!showCopiedMessage && "Copy all"}
                    {showCopiedMessage && "Copied!"}
                  </span>
                  &nbsp;&nbsp;
                  <span
                    className={`delete-text-style ${searchTerms?.length === 0 ? "disable-item" : ""}`}
                    onClick={deleteAllKeywordsHandler}
                  >
                    Delete all
                  </span>
                </div>
              )}
            </div>
          )}
        <div ref={inputElement} className={`wrapper-tags`}>
          {searchTerms ? (
            <div className="wrapper-content">
              <div className="keyword-wrapper scrollable">
                {inputFocused
                  ? searchTerms.map((keyword, index) => (
                    <SearchTerm
                      inAdvancedSearch={inAdvancedSearch}
                      key={index}
                      searchTerm={keyword}
                      searchTermId={keyword}
                      onRemove={handleOnRemove}
                      style={inAdvancedSearch ? "exclude" : ""}
                      includedKeywords={includedKeywords}
                      excludedKeywords={excludedKeywords}
                      title={keyword}
                      isToolTip
                    />
                  ))
                  : searchTerms.map((keyword, index) => {
                    if (index < 2) {
                      return (
                        <SearchTerm
                          inAdvancedSearch={inAdvancedSearch}
                          key={index}
                          searchTerm={keyword}
                          searchTermId={keyword}
                          onRemove={handleOnRemove}
                          style={inAdvancedSearch ? "exclude" : ""}
                          includedKeywords={includedKeywords}
                          excludedKeywords={excludedKeywords}
                          isToolTip
                        />
                      );
                    }
                  })}
                {!inputFocused && searchTerms.length > 2 ? (
                  <ExtendedKeywordCount onClick={() => setInputFocused(true)} count={searchTerms.length - 2} />
                ) : null}
              </div>
            </div>
          ) : null}

          {showInput === true ? (
            <Input
              inputRef={inputFieldRef}
              onFocus={() => setInputFocused(true)}
              // onBlur={() => inAdvancedSearch && setInputFocused(false)}
              onBlur={(e: any) => {
                if (value) handleAddSearchTerm(value);
                setValue("");
              }}
              type="text"
              value={value}
              className="search-input"
              placeholder={placeholder}
              onChange={(e: any) => (e.target.value !== "," ? setValue(e.target.value) : null)}
              onKeyPress={(e: any) => {
                if ((e.key === "Enter" || e.key === ",") && handleAddSearchTerm && !dynamicAudienceLoading) {
                  if (value) handleAddSearchTerm(value);
                  setValue("");
                }
                if (onKeyPressOpen && !firstKeyPressed) onKeyPressOpen()
              }}
              idInput={"start-typing-Keywords-to-find-matching-content-" + idprefix}
              inNewListAsider={inNewListAsider}
            />
          ) : null}
        </div>
        <div className="d-flex justify-content-between">
          <div>
            {errorMsgIncludedKeywordsEmpty &&
              (errorMsgIncludedKeywordsEmpty ? (
                <text className="error-msg-style">Add at least one keyword.</text>
              ) : null)}
            {errorIncludedKeywords &&
              (errorIncludedKeywords ? <text className="error-msg-style">{errorMsgIncludedKeywords}</text> : null)}
            {errorExcludedKeywords &&
              (errorExcludedKeywords ? <text className="error-msg-style">{errorMsgExcludedKeywords}</text> : null)}
          </div>
          {/* <div> {inAdvancedSearch && <div className=" keyword-count">{searchTerms?.length} total</div>}</div> */}
        </div>
        {/* {showCopiedMessage && <span className="copied-message">Copied to clipboard</span>} */}
        <p className={`dec-paragraph mb ${desClassName}`}>{desc}</p>
      </div>
    </Wrapper>
  );
};

export default KeywordSourceV2;

const Wrapper = styled.div<{
  marginTop?: string;
  marginBottom?: string;
  dynamicAudienceLoading?: boolean;
  inAdvanceSearch?: boolean;
  inNewListAsider?: boolean;
}>`
  opacity: ${(props) => (props.dynamicAudienceLoading ? "0.5" : "1")};
  pointer-events: ${(props) => (props.dynamicAudienceLoading ? "none" : "auto")};
  .error-msg-style {
    color: #ffa112;
    font-size: 12px;
  }
  .disable-item {
    pointer-events: none;
    opacity: 0.5;
  }
  .copied-message {
    color: #ffa112;
    font-size: 14px;
  }
  .add-words-wrapper {
    margin-top: ${(props) => (props.marginTop ? props.marginTop : "40px")};
    max-width: calc(100% - 5px);
    .wrapper-tags {
      background-color: #fff;
      border: 1px solid #dadada;
      border-radius: 10px;
      margin-top: 10px;
      padding: 2px 0;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        border-radius: 6px;
      }
      label {
        display: none;
      }
      .search-input {
        border: none;
        margin-bottom: 0;
        &:focus,
        &:active {
          box-shadow: none;
        }
      }
    }
    .wrapper-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0px 5px;
      white-space: break-spaces;
      .copy-text-style {
        background-color: #f8f8f8;
        border-radius: 5px;
        padding: 5px 10px;
        font-family: "Roboto";
        font-weight: 700;
        font-size: 14px;
        color: #3a4665;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 11px;
        }
        &:hover {
          cursor: pointer;
        }
      }
      .delete-text-style {
        font-family: "Roboto";
        font-weight: 700;
        font-size: 14px;
        color: #ae0000;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 11px;
        }
        &:hover {
          cursor: pointer;
        }
      }
      .keyword-wrapper {
        max-width: 100%;
        max-height: max-content;
        overflow: ${(props) => (props.inNewListAsider ? "auto" : "hidden")};
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          max-height: max-content;
        }
        .scrollable {
          max-height: calc(100vh - 200px);
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #001760 #efefef;
          ::-webkit-scrollbar {
            width: 5px;
            height: 5px;
            border-radius: 2px;
          }
          /* Track */
          ::-webkit-scrollbar-track {
            background: #efefef;
            margin-top: 10px;
            margin-bottom: 15px;
          }
          /* Handle */
          ::-webkit-scrollbar-thumb {
            background: #001760;
          }
          /* Handle on hover */
          ::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        }
      }
      .add-word-head {
        color: #5e7079;
        font-size: 20px;
        margin-bottom: 0px;
        font-family: "Roboto", sans-serif;
        font-weight: 600;
        display: inline-block;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 14px;
        }
      }
      .sub-text {
        font-size: 14px;
        margin-left: 2px;
        font-weight: 600;
        opacity: 0.4;
        width: auto;
        display: inline;
        color: rgba(0, 0, 0, 0.85);
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 10px;
        }
      }
      .test {
        font-weight: bold;
        font-size: 12px;
        color: #27ce0a;
        border: 1px solid #27ce0a;
        padding: 2px 8px;
        border-radius: 6px;
        cursor: pointer;
        letter-spacing: 1px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 9px;
          border-radius: 4px;
        }
        &.active {
          color: #fff;
          border: 1px solid #27ce0a;
          background-color: #27ce0a;
        }
      }
      .search-input {
        /* padding: 10px 18px;
      font-size: 16px;
      line-height: 20px;
      height: 38px; */
        width: 99%;
        margin-left: 3px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 12px;
          line-height: 16px;
          padding: 7px 10px;
          border-radius: 6px;
        }
      }
    }
    .dec-paragraph {
      color: #5e7079 !important;
      font-size: 14px;
      margin-bottom: 0px;
      font-family: "robotoregular";
      font-weight: 600;
      margin-top: 10px;
      opacity: 0.6;
      letter-spacing: -0.4px;
      white-space: pre-wrap;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
      }
      &.mb {
        margin-bottom: ${(props) => (props.marginBottom ? props.marginBottom : "40px")};
      }
      &.mb-s {
        margin-bottom: 20px !important;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-bottom: 10px !important;
        }
      }
    }
  }
`;
